import { useState, useEffect, useCallback } from "react";

const REDIS_SERVERS = {
  dev: { label:"TN-Dev",         url:"/redis-dev-api", accent:"#ef4444" },
  ps2: { label:"TN-PS-MQTT/TCP", url:"/redis-ps2-api", accent:"#f97316" },
};

export default function RedisPanel({ token, theme }) {
  const [server, setServer] = useState("dev");
  const [data,   setData]   = useState(null);
  const [loading,setLoading]= useState(true);
  const [selInst,setSelInst]= useState(0);
  const [selDb,  setSelDb]  = useState(null);
  const [dbKeys, setDbKeys] = useState([]);
  const [dbLoading, setDbLoading] = useState(false);

  const H   = { "X-Auth-Token": token };
  const cfg = REDIS_SERVERS[server];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${cfg.url}/redis`, { headers: H });
      setData(await r.json());
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [server, token]);

  useEffect(() => { 
    setData(null); setSelInst(0); setSelDb(null);
    load(); 
    const id = setInterval(load, 30000); 
    return ()=>clearInterval(id); 
  }, [load]);

  const loadDbKeys = async (dbNum) => {
    setSelDb(dbNum); setDbLoading(true); setDbKeys([]);
    try {
      const r = await fetch(`${cfg.url}/redis/keys?db=${dbNum}`, { headers: H });
      const d = await r.json();
      setDbKeys(d.patterns || []);
    } catch(e) { console.error(e); }
    setDbLoading(false);
  };

  const inst = data?.[selInst];
  const card = { background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"16px 20px" };

  const statCard = (label, value, color, sub) => (
    <div style={{...card,textAlign:"center",minWidth:130}}>
      <div style={{fontSize:10,fontWeight:700,color:theme.textMuted,letterSpacing:".1em",
        fontFamily:"'Inter',sans-serif",marginBottom:8}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color,fontFamily:"'Inter',sans-serif"}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginTop:4}}>{sub}</div>}
    </div>
  );

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0,
      background:theme.bg,fontFamily:"'Inter',sans-serif"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{padding:"12px 20px",borderBottom:`1px solid ${theme.border}`,
        background:theme.bgCard,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",flexShrink:0}}>
        <div style={{fontSize:16,fontWeight:800,color:theme.text}}>🔴 Redis Monitor</div>
        <div style={{width:1,height:20,background:theme.border}}/>
        {Object.entries(REDIS_SERVERS).map(([k,s])=>(
          <button key={k} onClick={()=>setServer(k)} style={{
            background:server===k?`${s.accent}18`:theme.inputBg,
            border:`1px solid ${server===k?`${s.accent}50`:theme.border}`,
            color:server===k?s.accent:theme.textMuted,
            borderRadius:6,padding:"5px 12px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
          }}>{s.label}</button>
        ))}
        <div style={{flex:1}}/>
        <button onClick={load} style={{
          background:`${cfg.accent}18`,border:`1px solid ${cfg.accent}50`,
          color:cfg.accent,borderRadius:6,padding:"5px 12px",cursor:"pointer",
          fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
        }}>↻ Refresh</button>
        {loading&&<div style={{width:16,height:16,borderRadius:"50%",
          border:`2px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
          animation:"spin .7s linear infinite"}}/>}
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:20,minHeight:0,paddingBottom:40}}>
        {!inst && loading && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            flexDirection:"column",gap:16,height:300}}>
            <div style={{width:48,height:48,borderRadius:"50%",
              border:`3px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
              animation:"spin .8s linear infinite"}}/>
            <div style={{fontSize:13,color:theme.textMuted}}>Loading Redis metrics...</div>
          </div>
        )}

        {!inst && !loading && (
          <div style={{textAlign:"center",padding:40,color:theme.textMuted,fontSize:13}}>
            ⚠️ No Redis data available. Check connection.
            <br/><button onClick={load} style={{marginTop:12,padding:"6px 16px",
              borderRadius:6,cursor:"pointer",background:cfg.accent,color:"#fff",
              border:"none",fontFamily:"'Inter',sans-serif",fontWeight:600}}>
              Retry
            </button>
          </div>
        )}
        {inst && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>

            {/* Instance selector if multiple */}
            {data.length > 1 && (
              <div style={{display:"flex",gap:8}}>
                {data.map((d,i)=>(
                  <button key={i} onClick={()=>setSelInst(i)} style={{
                    background:selInst===i?`${cfg.accent}18`:theme.inputBg,
                    border:`2px solid ${selInst===i?cfg.accent:theme.border}`,
                    color:selInst===i?cfg.accent:theme.textMuted,
                    borderRadius:8,padding:"6px 14px",cursor:"pointer",
                    fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
                  }}>
                    <span style={{width:6,height:6,borderRadius:"50%",display:"inline-block",
                      marginRight:6,background:d.status==="online"?"#10b981":"#ef4444"}}/>
                    {d.name} :{d.port}
                  </button>
                ))}
              </div>
            )}

            {/* Stat cards */}
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {statCard("STATUS",
                inst.status==="online"?"● ONLINE":"● OFFLINE",
                inst.status==="online"?"#10b981":"#ef4444",
                `v${inst.version} · up ${inst.uptime}`)}
              {statCard("MEMORY USED", inst.memory.used, cfg.accent, `peak: ${inst.memory.peak}`)}
              {statCard("MAX MEMORY", inst.memory.max, "#f59e0b",
                `frag: ${inst.memory.fragmentation}x`)}
              {statCard("CONNECTED", inst.clients.connected, "#06b6d4",
                `max: ${inst.clients.max}`)}
              {statCard("HIT RATE",
                `${inst.stats.hit_rate}%`,
                inst.stats.hit_rate>=90?"#10b981":inst.stats.hit_rate>=70?"#f59e0b":"#ef4444",
                `${inst.stats.keyspace_hits} hits`)}
              {statCard("TOTAL KEYS", inst.stats.total_keys.toLocaleString(), "#a78bfa", "all databases")}
              {statCard("EVICTED", inst.stats.evicted_keys, inst.stats.evicted_keys>0?"#ef4444":"#10b981",
                `expired: ${inst.stats.expired_keys}`)}
            </div>

            {/* Memory bar */}
            {inst.memory.max !== "No limit" && (
              <div style={card}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:12}}>💾 MEMORY USAGE</div>
                <div style={{background:theme.border,borderRadius:6,height:12,overflow:"hidden"}}>
                  <div style={{
                    width:`${Math.min(parseFloat(inst.memory.used)/parseFloat(inst.memory.max)*100,100)}%`,
                    height:"100%",background:cfg.accent,borderRadius:6,transition:"width .3s"
                  }}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                  <span style={{fontSize:11,color:theme.textMuted}}>Used: {inst.memory.used}</span>
                  <span style={{fontSize:11,color:theme.textMuted}}>Max: {inst.memory.max}</span>
                </div>
              </div>
            )}

            {/* Databases */}
            <div style={card}>
              <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                letterSpacing:".1em",marginBottom:14}}>🗄 DATABASES</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
                {inst.databases.map((db,i)=>{
                  const pct = inst.stats.total_keys>0
                    ? Math.round(db.keys/inst.stats.total_keys*100) : 0;
                  return (
                    <div key={db.db}
                      onClick={()=>loadDbKeys(db.db.replace("db",""))}
                      style={{background:selDb===db.db.replace("db","")?`${cfg.accent}10`:theme.inputBg,
                        borderRadius:10,padding:"12px 16px",
                        border:`2px solid ${selDb===db.db.replace("db","")?cfg.accent:theme.border}`,
                        cursor:"pointer",transition:"all .2s"}}>
                      <div style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",marginBottom:8}}>
                        <span style={{fontSize:13,fontWeight:700,color:theme.text,
                          fontFamily:"'JetBrains Mono',monospace"}}>{db.db}</span>
                        <span style={{fontSize:11,color:cfg.accent,fontWeight:700,
                          fontFamily:"'JetBrains Mono',monospace"}}>
                          {db.keys.toLocaleString()} keys
                        </span>
                      </div>
                      <div style={{background:theme.border,borderRadius:4,height:4}}>
                        <div style={{width:`${pct}%`,height:"100%",borderRadius:4,
                          background:cfg.accent}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                        <span style={{fontSize:10,color:theme.textMuted}}>{pct}% of total</span>
                        <span style={{fontSize:10,color:theme.textMuted}}>
                          {db.expires} expiring
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* DB Keys Detail */}
            {selDb !== null && (
              <div style={card}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,letterSpacing:".1em"}}>
                    🔑 db{selDb} KEY PATTERNS
                  </div>
                  <button onClick={()=>{setSelDb(null);setDbKeys([]);}} style={{
                    background:"transparent",border:"none",color:theme.textMuted,
                    cursor:"pointer",fontSize:16}}>✕</button>
                </div>
                {dbLoading && <div style={{fontSize:12,color:theme.textMuted,fontStyle:"italic"}}>Loading keys...</div>}
                {!dbLoading && dbKeys.length===0 && <div style={{fontSize:12,color:theme.textMuted,fontStyle:"italic"}}>No patterns found</div>}
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {dbKeys.map((p,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:8,
                      background:theme.inputBg,border:`1px solid ${theme.border}`,
                      borderRadius:8,padding:"8px 14px"}}>
                      <span style={{fontSize:12,color:theme.text,fontFamily:"'JetBrains Mono',monospace"}}>{p.pattern}</span>
                      <span style={{fontSize:11,fontWeight:700,color:cfg.accent,
                        background:`${cfg.accent}15`,padding:"2px 8px",borderRadius:5}}>
                        {p.count.toLocaleString()} keys
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats grid */}
            <div style={{...card}}>
              <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                letterSpacing:".1em",marginBottom:14}}>📊 PERFORMANCE STATS</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[
                  ["Total Commands", inst.stats.total_commands.toLocaleString()],
                  ["Keyspace Hits", inst.stats.keyspace_hits.toLocaleString()],
                  ["Keyspace Misses", inst.stats.keyspace_misses.toLocaleString()],
                  ["Hit Rate", `${inst.stats.hit_rate}%`],
                  ["Evicted Keys", inst.stats.evicted_keys],
                  ["Expired Keys", inst.stats.expired_keys],
                  ["Blocked Clients", inst.clients.blocked],
                  ["Memory Fragmentation", `${inst.memory.fragmentation}x`],
                ].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",
                    padding:"7px 10px",borderRadius:6,background:theme.inputBg}}>
                    <span style={{fontSize:12,color:theme.textMuted}}>{k}</span>
                    <span style={{fontSize:12,fontWeight:700,color:theme.text,
                      fontFamily:"'JetBrains Mono',monospace"}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Connection Details */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {/* By IP */}
              <div style={card}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:14}}>🌐 CONNECTIONS BY IP</div>
                {(inst.clients.by_ip||[]).length===0 ? (
                  <div style={{fontSize:12,color:theme.textMuted,fontStyle:"italic"}}>No external connections</div>
                ) : (inst.clients.by_ip||[]).map((c,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",padding:"8px 10px",borderRadius:6,marginBottom:4,
                    background:i%2===0?theme.inputBg:"transparent"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:"#10b981",flexShrink:0}}/>
                      <span style={{fontSize:12,color:theme.text,fontFamily:"'JetBrains Mono',monospace"}}>{c.ip}</span>
                    </div>
                    <span style={{fontSize:13,fontWeight:700,color:cfg.accent,
                      background:`${cfg.accent}15`,padding:"2px 8px",borderRadius:5,
                      fontFamily:"'JetBrains Mono',monospace"}}>{c.count} conn</span>
                  </div>
                ))}
              </div>
              {/* By Command */}
              <div style={card}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:14}}>⚡ COMMANDS IN USE</div>
                {(inst.clients.by_command||[]).map((c,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",padding:"8px 10px",borderRadius:6,marginBottom:4,
                    background:i%2===0?theme.inputBg:"transparent"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:"#f59e0b",flexShrink:0}}/>
                      <span style={{fontSize:12,color:theme.text,fontFamily:"'JetBrains Mono',monospace"}}>{c.cmd}</span>
                    </div>
                    <span style={{fontSize:13,fontWeight:700,color:"#f59e0b",
                      background:"rgba(245,158,11,0.15)",padding:"2px 8px",borderRadius:5,
                      fontFamily:"'JetBrains Mono',monospace"}}>{c.count}</span>
                  </div>
                ))}
              </div>
            </div>



            {/* Connected clients list */}
            {inst.clients.list?.length > 0 && (
              <div style={card}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:14}}>
                  🔌 CLIENT LIST ({inst.clients.connected})
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {inst.clients.list.filter(c=>!c.ip.startsWith("127.")).map((c,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:12,
                      padding:"6px 10px",borderRadius:6,
                      background:i%2===0?theme.inputBg:"transparent"}}>
                      <div style={{width:6,height:6,borderRadius:"50%",
                        background:"#10b981",flexShrink:0}}/>
                      <span style={{fontSize:12,color:theme.text,flex:1,
                        fontFamily:"'JetBrains Mono',monospace"}}>{c.addr}</span>
                      <span style={{fontSize:11,color:theme.textMuted,
                        background:theme.badge,padding:"2px 6px",borderRadius:4}}>db{c.db}</span>
                      <span style={{fontSize:11,color:cfg.accent,
                        fontFamily:"'JetBrains Mono',monospace",minWidth:40}}>{c.cmd}</span>
                      <span style={{fontSize:11,color:theme.textMuted}}>idle:{c.idle}s</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
